import React, { useState, useRef, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { AnimatePresence, motion } from 'framer-motion';
import { functions } from '../utils/firebase';

const chatFn = httpsCallable(functions, 'chat', { timeout: 30000 });

const WELCOME_ID = 'welcome';
const WELCOME_MSG = {
  id: WELCOME_ID,
  role: 'assistant',
  content: "Hi! I'm KhojaBot — your Khoja Distributions assistant. I can help with product questions, wholesale pricing, or getting your account set up. What can I help you with today?",
};

const QUICK_PROMPTS = [
  'What products do you carry?',
  'How do I apply for wholesale?',
  'Do you deliver to my area?',
];

// Tracks the real visible height + keyboard offset (fixes iOS Safari keyboard bug)
function useVisualViewport() {
  const getSnap = () => ({
    vpHeight:   window.visualViewport?.height   ?? window.innerHeight,
    vpOffsetTop: window.visualViewport?.offsetTop ?? 0,
    isMobile:   window.innerWidth < 640,
  });

  const [vp, setVp] = useState(getSnap);

  useEffect(() => {
    const update = () => setVp(getSnap());
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return vp;
}

function BotAvatar() {
  return (
    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-sky-600 to-slate-800 flex items-center justify-center flex-shrink-0 shadow-sm">
      <span className="text-white text-xs font-bold font-heading">K</span>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-end gap-2">
      <BotAvatar />
      <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-xs">
        <div className="flex gap-1.5 items-center h-4">
          {[0, 160, 320].map(d => (
            <span key={d} className="w-2 h-2 rounded-full bg-slate-300 animate-bounce"
              style={{ animationDelay: `${d}ms` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductCards({ products }) {
  if (!products?.length) return null;
  return (
    <div className="flex flex-col gap-1.5 mt-1.5">
      {products.map(p => (
        <a
          key={p.id}
          href={`/products/details/${p.id}`}
          className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-3 py-2 hover:border-sky-300 hover:shadow-sm transition-all duration-150 group"
        >
          <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
            {p.imageUrl ? (
              <img src={p.imageUrl} alt={p.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-700 truncate">{p.name}</p>
            {p.category && <p className="text-[10px] text-slate-400 mt-0.5">{p.category}</p>}
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-sky-400 flex-shrink-0 group-hover:text-sky-500 transition-colors" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </a>
      ))}
    </div>
  );
}

function OptionChips({ options, onSelect }) {
  if (!options?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => onSelect(opt)}
          className="text-xs border border-sky-200 text-sky-700 bg-sky-50 hover:bg-sky-100 hover:border-sky-300 px-3 py-1.5 rounded-full transition-all duration-150 font-medium active:scale-95"
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// Converts **bold**, *italic*, and - bullet lines into React elements
function renderInline(text, key) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*'))
      return <em key={i}>{part.slice(1, -1)}</em>;
    return part;
  });
}

function BotMessageContent({ content }) {
  const lines = (content || '').split('\n');
  const elements = [];
  let listItems = [];

  const flushList = (k) => {
    if (!listItems.length) return;
    elements.push(
      <ul key={k} className="mt-1 mb-0.5 space-y-1">
        {listItems.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0" />
            <span>{renderInline(item)}</span>
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      listItems.push(trimmed.slice(2).trim());
    } else {
      flushList(`list-${i}`);
      if (trimmed) elements.push(<p key={i}>{renderInline(trimmed)}</p>);
    }
  });
  flushList('list-end');

  return <div className="space-y-1 text-sm leading-relaxed">{elements}</div>;
}

function Message({ msg, onOptionSelect }) {
  const isUser = msg.role === 'user';
  const hasProducts = !isUser && msg.products?.length > 0;
  const hasOptions = !isUser && msg.options?.length > 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={`flex flex-col gap-0.5 ${isUser ? 'items-end' : 'items-start'}`}
    >
      <div className={`flex items-end gap-2 w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
        {!isUser && <BotAvatar />}
        <div className={`max-w-[78%] px-4 py-2.5 ${
          isUser
            ? 'text-sm leading-relaxed whitespace-pre-wrap bg-gradient-to-br from-sky-600 to-sky-700 text-white rounded-2xl rounded-br-sm shadow-blue'
            : 'text-slate-700 bg-white border border-slate-100 rounded-2xl rounded-bl-sm shadow-xs'
        }`}>
          {isUser ? msg.content : <BotMessageContent content={msg.content} />}
        </div>
      </div>
      {(hasProducts || hasOptions) && (
        <div className="pl-9 w-full pr-1">
          {hasProducts && <ProductCards products={msg.products} />}
          {hasOptions && <OptionChips options={msg.options} onSelect={onOptionSelect} />}
        </div>
      )}
    </motion.div>
  );
}

function ChatHeader({ onClose }) {
  return (
    <div
      className="flex items-center justify-between px-4 py-3 flex-shrink-0"
      style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)' }}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-semibold font-heading tracking-wide">KhojaBot</span>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
            </span>
          </div>
          <p className="text-slate-400 text-[11px] leading-none mt-0.5">Khoja Distributions · Online</p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-300 hover:text-white transition-all duration-150"
        aria-label="Close chat"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
}

export default function ChatWidget() {
  const [isOpen, setIsOpen]       = useState(false);
  const [messages, setMessages]   = useState([WELCOME_MSG]);
  const [input, setInput]         = useState('');
  const [isTyping, setIsTyping]   = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  const { vpHeight, vpOffsetTop, isMobile } = useVisualViewport();
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  const sendMessage = useCallback(async (textOverride) => {
    const text = (typeof textOverride === 'string' ? textOverride : input).trim();
    if (!text || isTyping) return;

    const userMsg = { id: Date.now(), role: 'user', content: text };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setIsTyping(true);

    try {
      const history = next
        .filter(m => m.id !== WELCOME_ID)
        .map(m => ({ role: m.role, content: m.content }));

      const result = await chatFn({ messages: history });
      const reply = result.data?.reply
        || "I'm having trouble responding. Please try again or visit our /contact page.";
      const products = result.data?.products || [];
      const options = result.data?.options || [];

      setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', content: reply, products, options }]);
      if (!isOpen) setHasUnread(true);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: "Sorry, I'm having trouble connecting right now. Please try again or visit our /contact page.",
      }]);
    } finally {
      setIsTyping(false);
    }
  }, [input, messages, isTyping, isOpen]);

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showQuickPrompts = messages.length === 1 && !isTyping;

  // iOS Safari: when keyboard opens, visualViewport scrolls up inside window.
  // keyboardOffset = how far the keyboard encroaches from the bottom of the window.
  const keyboardOffset = Math.max(
    0,
    window.innerHeight - vpHeight - vpOffsetTop
  );

  // ── Mobile bottom-sheet geometry ──────────────────────────────────────────
  // Height = visible area minus a small top gap (56px) so page peeks through.
  // Bottom is lifted above the keyboard via keyboardOffset.
  const mobileSheetHeight = vpHeight - 56;

  const mobileWindowStyle = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: keyboardOffset,
    height: mobileSheetHeight,
    borderRadius: '20px 20px 0 0',
    boxShadow: '0 -10px 40px -10px rgba(15,23,42,0.25)',
    zIndex: 50,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid rgba(226,232,240,0.8)',
  };

  const desktopWindowStyle = {
    width: 'min(380px, calc(100vw - 2rem))',
    maxHeight: 'min(570px, calc(100vh - 8rem))',
    boxShadow: '0 20px 60px -10px rgba(15,23,42,0.25), 0 4px 16px -4px rgba(15,23,42,0.12)',
  };

  return (
    <>
      {/* ── Chat window ── */}
      <AnimatePresence>
        {isOpen && isMobile && (
          // Mobile: bottom sheet slides up
          <motion.div
            key="chat-mobile"
            style={mobileWindowStyle}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <ChatHeader onClose={() => setIsOpen(false)} />

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/80 overscroll-contain">
              {messages.map(msg => <Message key={msg.id} msg={msg} onOptionSelect={sendMessage} />)}
              {isTyping && <TypingDots />}
              <div ref={bottomRef} />
            </div>

            <AnimatePresence>
              {showQuickPrompts && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-4 pt-2 pb-1 bg-slate-50/80 flex flex-wrap gap-1.5 border-t border-slate-100"
                >
                  {QUICK_PROMPTS.map(p => (
                    <button key={p} onClick={() => { setInput(p); inputRef.current?.focus(); }}
                      className="text-xs border border-sky-200 text-sky-700 bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-full transition-all font-medium">
                      {p}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="bg-white border-t border-slate-100 px-3 py-3 flex gap-2 items-center flex-shrink-0">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything…"
                disabled={isTyping}
                // Prevents iOS from zooming in on focus (font-size must be >= 16px)
                style={{ fontSize: 16 }}
                className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400 disabled:opacity-60 transition-all placeholder:text-slate-400"
              />
              <button onClick={sendMessage} disabled={!input.trim() || isTyping}
                aria-label="Send"
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40 active:scale-90"
                style={{ background: input.trim() && !isTyping ? 'linear-gradient(135deg,#0369A1,#0284C7)' : '#E2E8F0' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${input.trim() && !isTyping ? 'text-white' : 'text-slate-400'}`} viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </div>

            <div className="bg-white pb-safe flex items-center justify-center py-2">
              <span className="text-[10px] text-slate-300 font-medium">Powered by&nbsp;</span>
              <span className="text-[10px] text-slate-400 font-semibold">Claude AI</span>
            </div>
          </motion.div>
        )}

        {isOpen && !isMobile && (
          // Desktop: floating window
          <motion.div
            key="chat-desktop"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 z-50 flex flex-col rounded-2xl overflow-hidden border border-slate-200/80"
            style={desktopWindowStyle}
          >
            <ChatHeader onClose={() => setIsOpen(false)} />

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/80">
              {messages.map(msg => <Message key={msg.id} msg={msg} onOptionSelect={sendMessage} />)}
              {isTyping && <TypingDots />}
              <div ref={bottomRef} />
            </div>

            <AnimatePresence>
              {showQuickPrompts && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-4 pt-2 pb-1 bg-slate-50/80 flex flex-wrap gap-1.5 border-t border-slate-100"
                >
                  {QUICK_PROMPTS.map(p => (
                    <button key={p} onClick={() => { setInput(p); inputRef.current?.focus(); }}
                      className="text-xs border border-sky-200 text-sky-700 bg-sky-50 hover:bg-sky-100 hover:border-sky-300 px-3 py-1.5 rounded-full transition-all duration-150 font-medium">
                      {p}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="bg-white border-t border-slate-100 px-3 py-3 flex gap-2 items-center flex-shrink-0">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything…"
                disabled={isTyping}
                className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-full px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400 disabled:opacity-60 transition-all placeholder:text-slate-400"
              />
              <button onClick={sendMessage} disabled={!input.trim() || isTyping}
                aria-label="Send message"
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                style={{ background: input.trim() && !isTyping ? 'linear-gradient(135deg,#0369A1,#0284C7)' : '#E2E8F0' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${input.trim() && !isTyping ? 'text-white' : 'text-slate-400'}`} viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </div>

            <div className="bg-white px-4 pb-2.5 flex items-center justify-center gap-1">
              <span className="text-[10px] text-slate-300 font-medium">Powered by</span>
              <span className="text-[10px] text-slate-400 font-semibold">Claude AI</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Floating button (hidden on mobile when chat is open) ── */}
      {(!isMobile || !isOpen) && (
        <motion.button
          onClick={() => setIsOpen(prev => !prev)}
          aria-label={isOpen ? 'Close chat' : 'Open chat'}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.93 }}
          className="fixed bottom-4 right-4 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-brand-lg"
          style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)' }}
        >
          {!isOpen && (
            <span className="absolute w-full h-full rounded-full border-2 border-sky-500/40 animate-ping" />
          )}
          {hasUnread && !isOpen && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
              1
            </span>
          )}
          <AnimatePresence mode="wait" initial={false}>
            {isOpen ? (
              <motion.svg key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}
                xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </motion.svg>
            ) : (
              <motion.svg key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}
                xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </motion.svg>
            )}
          </AnimatePresence>
        </motion.button>
      )}
    </>
  );
}
